import { Icon } from "@iconify/react";
import { ArrowLeftFromLine } from "lucide-react";
import { useAuthStore } from "../../../store/useAuthStore";
import { HStack, PlatformEnum, Error } from "../../../shared/utils";
import { useGetPlatformInfo, useGetUserProfile } from "../../../queries/auth";
import { Input, Label, Required } from "../../../shared/InputField";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { handleClickForTracking } from "../../../analytics";
import { AnalyticsConstant } from "../../../AnalyticsContant";
import { updateUserInfo } from "../../../api/auth";
import ChangePasswordModal from "./ChangePasswordModal";
import { PasskeyManagement } from "./PasskeyManagement";
import {
  ButtonSize,
  IconCTAButton,
  PrimaryCTAButton,
} from "../../../shared/CTAButtonComponents";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import Loader from "../../../utils/loader";
import { AuthStrategyType } from "../../../types/auth";
import { trackEvent } from "../../../utils/AmpAnalytics";
import { useFeatureFlag } from "../../../FeatureFlags";

export type UserProfileType = {
  name: string;
  email?: string;
  phoneNumber?: string;
  authStrategies?: string[];
  profilePicture?: File | null;
};

export function UserProfilePage() {
  const { email, name } = useAuthStore();
  const {
    data,
    isLoading: isDataLoading,
    refetch: refetchUserProfile,
  } = useGetUserProfile();
  const { accessToken, setAuth } = useAuthStore();
  const fileInputRef = useRef<any>(null);
  const isPasskeyEnabled = useFeatureFlag("enablePasskey");
  const [dialogChangePassword, setDialogChangePassword] = useState(false);
  const hasFetchedRef = useRef(false);
  const navigate = useNavigate();
  const {
    data: userPlatformData,
    refetch,
    isLoading,
  } = useGetPlatformInfo(accessToken || "", false);
  const [profileData, setProfileData] = useState<any>(data);
  const profileImage =
    (userPlatformData && userPlatformData[0].profilePicUrl) || undefined;
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<any>(null);
  const [passkeyCount, setPasskeyCount] = useState<number | undefined>(undefined);
  // /profile exposes isPasswordSet directly; unlike /userInfo it does not
  // include isApplicationSetPassword.
  const hidePasswordOption = profileData?.isPasswordSet !== true;
  const options = [
    ...(!hidePasswordOption
      ? [{ label: "Password", value: AuthStrategyType.PASSWORD }]
      : []),
    { label: "Email OTP", value: AuthStrategyType.EMAIL_OTP },
    { label: "Google", value: AuthStrategyType.GOOGLE },
    { label: "Microsoft", value: AuthStrategyType.MICROSOFT },
    ...(isPasskeyEnabled && passkeyCount !== 0
      ? [{ label: "Passkey", value: AuthStrategyType.PASSKEY }]
      : []),
  ];

  useEffect(() => {
    if (accessToken && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      refetch();
    }
  }, [accessToken]);
  useEffect(() => {
    if (!isDataLoading && data?.data) {
      setProfileData(data?.data);
      const hidePasswordOption = data?.data?.isPasswordSet !== true;
      const authStrategies = hidePasswordOption
        ? (data?.data?.authStrategies || []).filter(
            (strategy: string) => strategy !== AuthStrategyType.PASSWORD
          )
        : data?.data?.authStrategies;
      setSelectedItems(authStrategies);
    }
  }, [data, isDataLoading]);
  const initialValues: UserProfileType = profileData
    ? {
        email: profileData.email || "",
        name: profileData.name || "",
        phoneNumber: profileData.phoneNumber || "",
        authStrategies:
          hidePasswordOption
            ? (profileData.authStrategies || []).filter(
                (strategy: string) => strategy !== AuthStrategyType.PASSWORD
              )
            : profileData.authStrategies || [],
        profilePicture: profileData.profilePicture || null,
      }
    : {
        email: email ?? "",
        name: name ?? "",
        phoneNumber: "",
        authStrategies: [],
        profilePicture: null,
      };

  const userProfileSchema = Yup.object().shape({
    name: Yup.string().required().label("Full Name"),
    authStrategies: Yup.array().min(1, "At least One method is required"),
  });

  async function handleFileUpload(authStrategiesOverride?: string[], isPasskeyChange?: boolean) {
    setLoading(true);
    const finalData: UserProfileType = {
      email: email,
      name: formik.values.name,
      phoneNumber: formik.values.phoneNumber?.toString(),
      authStrategies: authStrategiesOverride ?? formik.values.authStrategies,
      profilePicture: selectedFile,
    };
    const formData = new FormData();
    if (selectedFile) {
      formData.append("file", selectedFile);
    }
    formData.append("data", JSON.stringify(finalData));

    try {
      const baseUrl =
        process.env.REACT_APP_API || "https://dev-identity-api.hissa.com/v1/";
      const response = await fetch(`${baseUrl}auth/user/profile`, {
        method: "PUT",
        headers: {
          AccessToken: `${accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const result = await response.json();

        toast(result.message, {
          type: "error",
          autoClose: 2000,
        });
      }
      setShowImageUpload(false);
      handleReset();
      trackEvent("Updating Profile");
      setAuth((prev: any) => ({ ...prev, name: formik.values.name }));
      if (!isPasskeyChange) {
      toast("Profile Updated Successfully", {
        type: "success",
        autoClose: 2000,
      });
    }
      refetch();
      refetchUserProfile();
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      toast(error, {
        type: "error",
        autoClose: 2000,
      });
    }
  }

  const formik = useFormik({
    initialValues,
    validationSchema: userProfileSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      handleFileUpload();
    },
  });
  const [selectedItems, setSelectedItems] = useState<string[]>(
    formik.values.authStrategies || []
  );

  function getCurrentPlatformData(platform: string) {
    const currentPlatform = Array.isArray(userPlatformData)
      ? userPlatformData.find(
          (obj: any) => obj.platform?.toLowerCase() === platform.toLowerCase()
        )
      : undefined;
    return currentPlatform?.data;
  }

  const handleCheckboxChange = (option: string) => {
    setSelectedItems((prev: string[]) =>
      prev.includes(option)
        ? prev.filter((item) => item !== option)
        : [...prev, option]
    );
  };

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      minHeight: "2.5rem",
    }),
  };

  useEffect(() => {
    formik.setFieldValue("authStrategies", selectedItems);
  }, [selectedItems]);

  const platforms = [
    {
      name: "CAPTABLE",
      icon: "/media/switchProductIcons/captable.svg",
      redirectURL: process.env.REACT_APP_CAPTABLE_URL,
      bgColor: "#E6EFFF",
      access:
        (getCurrentPlatformData("captable") &&
          getCurrentPlatformData("captable")?.access) ||
        false,
      platform: PlatformEnum.captable,
      analytics: AnalyticsConstant.captable_redirect_button,
    },
    {
      name: "ESOP",
      icon: "/media/switchProductIcons/esop.svg",
      redirectURL: process.env.REACT_APP_ESOP_URL,
      bgColor: "#FAF9F3",
      access:
        (getCurrentPlatformData("esop") &&
          getCurrentPlatformData("esop")?.access) ||
        false,
      platform: PlatformEnum.esop,
      analytics: AnalyticsConstant.esop_redirect_button,
    },
    {
      name: "PORTFOLIO",
      icon: "/media/switchProductIcons/portfolio.svg",
      redirectURL: process.env.REACT_APP_INVESTOR_URL,
      bgColor: "#E9FBF3",
      access:
        (getCurrentPlatformData("investor") &&
          getCurrentPlatformData("investor")?.access) ||
        false,
      platform: PlatformEnum.investor,
      analytics: AnalyticsConstant.investor_redirect_button,
    },
    {
      name: "TASK MANAGER",
      icon: "/media/switchProductIcons/task_manager.svg",
      className: "px-2",
      bgColor: "#FEF2E7",
      redirectURL: process.env.REACT_APP_TASKMANAGER_URL,
      access:
        (getCurrentPlatformData("taskmanager") &&
          getCurrentPlatformData("taskmanager")?.access) ||
        false,
      platform: PlatformEnum.taskManager,
      analytics: AnalyticsConstant.taskManager_redirect_button,
    },
  ];

  const handleRedirect = (
    redirectURL: string,
    platform: string,
    analytics: string
  ) => {
    const redirectKey = `${platform}_redirect_button`;
    const access =
      getCurrentPlatformData(platform) &&
      getCurrentPlatformData(platform)?.access;
    if (access && access === true) {
      if (redirectKey in AnalyticsConstant) {
        // handleClickForTracking(AnalyticsConstant[redirectKey]);
        trackEvent(`Navigating To ${platform}`);
      }
      sessionStorage.clear();
      updateUserInfo({ data: { lastUsedProduct: platform as PlatformEnum } });
      window.location.href = `${redirectURL}`;
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setImagePreview(null);
  };

  const handlePasskeyAdded = () => {
    const current = formik.values.authStrategies || [];
    if (!current.includes(AuthStrategyType.PASSKEY)) {
      const updated = [...current, AuthStrategyType.PASSKEY];
      formik.setFieldValue("authStrategies", updated);
      handleFileUpload(updated, true);
    }
  };

  const handleLastPasskeyRemoved = () => {
    const updated = (formik.values.authStrategies || []).filter(
      (s) => s !== AuthStrategyType.PASSKEY
    );
    formik.setFieldValue("authStrategies", updated);
    handleFileUpload(updated, true);
  };

  const handleUploadClick = () => {
    const fileInput: any = fileInputRef.current;
    if (fileInput) fileInput.click();
  };

  return (
    <>
      <HStack className="flex flex-row mt-6">
        <div
          className="flex flex-row items-center gap-2 hover:text-primary cursor-pointer border rounded-md px-1"
          onClick={() => {
            navigate("/landing/dashboard");
            trackEvent("Navigating to Dashboard");
          }}
        >
          <ArrowLeftFromLine />
          <p className="font-semibold w-auto py-1">Dashboard</p>
        </div>
      </HStack>

      {dialogChangePassword && (
        <ChangePasswordModal
          isOpen={dialogChangePassword}
          hasPassword={!hidePasswordOption}
          onPasswordUpdated={() => refetchUserProfile()}
          onClose={() => {
            setDialogChangePassword(false);
          }}
        />
      )}

      {showImageUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white mx-6 md:mx-0 p-6 rounded-lg shadow-lg w-[450px] space-y-4 relative">
            <h2 className="text-lg font-semibold">Upload Profile Image</h2>
            {imagePreview && (
              <div className="flex justify-center">
                <div className="relative w-36 h-32 border rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            )}

            <div>
              <IconCTAButton
                value={"Upload Image"}
                onClick={handleUploadClick}
                iconName={"material-symbols:upload-rounded"}
                className={`w-full flex items-center justify-center hover:bg-blue/10`}
                buttonSize={ButtonSize.MEDIUM}
              />
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept="image/png, image/jpeg"
                onChange={handleImageUpload}
              />
            </div>

            <div className="flex justify-between gap-2">
              <button
                onClick={() => {
                  setShowImageUpload(false);
                  handleReset();
                }}
                className="px-4 py-2 text-sm text-gray-600 border rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <PrimaryCTAButton
                event-name="Email Continue"
                className="w-28 text-center px-auto "
                buttonSize={ButtonSize.SMALL}
                loading={loading}
                disabled={selectedFile === null}
                onClick={() => formik.handleSubmit()}
                type="submit"
              >
                <p className="w-full text-sm leading-0"> Upload </p>
              </PrimaryCTAButton>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white w-full h-auto mt-4 rounded-2xl pb-6">
        <div className="rounded-t-lg h-[110px] bg-gradient-to-r from-blue-200 to-orange-100 "></div>
        <div className="flex flex-col md:flex-row justify-between p-4 md:p-8 gap-6 md:items-center">
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-center">
            <div className="relative">
              {profileImage ? (
                <div className="relative w-24 h-24  border rounded-lg overflow-hidden">
                  <img
                    src={profileImage}
                    alt="Profile"
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : isLoading ? (
                <div className="w-24 h-24 border bg-blue-100 rounded-lg flex items-center justify-center shadow-sm">
                  <Loader />
                </div>
              ) : (
                <div className="w-24 h-24 border bg-blue-100 rounded-lg flex items-center justify-center shadow-sm">
                  <Icon
                    icon="gridicons:user"
                    className="text-gray-700 placeholderLoader"
                    height={50}
                    width={50}
                  />
                </div>
              )}
              <button
                className="absolute -bottom-1 -right-1 bg-white p-1 rounded-full shadow-md"
                onClick={() => {
                  setShowImageUpload(true);
                  setImagePreview(profileImage);
                }}
              >
                <Icon
                  icon="ic:outline-edit"
                  className="text-gray-800 placeholderLoader"
                  height={16}
                />
              </button>
            </div>
            <div className="text-center sm:text-left">
              <h2 className="text-xl font-medium">{profileData?.name}</h2>
              <p className="text-gray-400">{profileData?.email}</p>
            </div>
          </div>

          <div className="flex gap-2 justify-center md:justify-end">
            <button
              className="border border-orange-500 text-orange-500 px-4 py-2 rounded-md text-sm hover:bg-orange-50"
              onClick={() => {
                setDialogChangePassword(true);
              }}
            >
              {hidePasswordOption
                ? "Create Password"
                : "Change Password"}
            </button>

            <PrimaryCTAButton
              event-name="Email Continue"
              className="w-28 text-center px-auto"
              buttonSize={ButtonSize.SMALL}
              loading={loading}
              onClick={() => {
                formik.handleSubmit();
              }}
              type="submit"
            >
              <p className="w-full text-sm leading-0"> Save </p>
            </PrimaryCTAButton>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 m-8">
          <div>
            <Label>
              Full Name <Required />
            </Label>
            <Input
              className="w-full pr-10"
              type={"text"}
              placeholder="Full Name"
              {...formik.getFieldProps("name")}
            />
            {formik.touched?.name && formik.errors?.name && (
              <Error text={formik.errors?.name} />
            )}
          </div>
          <div>
            <Label>Email Address</Label>

            <Input
              className="w-full pr-10"
              type={"text"}
              placeholder="Email"
              disabled={true}
              value={formik.values.email || email}
            />
          </div>
          <div>
            <Label>Phone Number</Label>
            <Input
              className="w-full pr-10"
              type={"number"}
              placeholder="Phone Number"
              {...formik.getFieldProps("phoneNumber")}
            />
          </div>

          <div>
            <Label>Login Methods</Label>
            <Select
              isMulti
              name="authStrategies"
              options={options}
              className="py-2 cursor-pointer"
              classNamePrefix="select"
              styles={customStyles}
              value={options.filter((opt) =>
                formik.values.authStrategies?.includes(opt.value)
              )}
              onChange={(selected) => {
                const values = selected.map((opt) => opt.value);
                formik.setFieldValue("authStrategies", values);
              }}
              onBlur={() => formik.setFieldTouched("authStrategies", true)}
            />

            {formik.touched?.authStrategies &&
              formik.errors?.authStrategies && (
                <Error text={formik.errors?.authStrategies} />
              )}
          </div>
        </div>

        {isPasskeyEnabled && (
          <div className="mx-8">
            <PasskeyManagement
              onPasskeyAdded={handlePasskeyAdded}
              onLastPasskeyRemoved={handleLastPasskeyRemoved}
              onPasskeysCountChange={setPasskeyCount}
            />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 m-8">
          {/* <div>
            <Label>Login Methods</Label>
            <div className="space-y-2 mt-2">
              {options.map((option) => (
                <label
                  key={option.label}
                  className="flex items-center space-x-3 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="form-checkbox h-4 w-4 text-blue-600 cursor-pointer"
                    checked={selectedItems.includes(option.value)}
                    onChange={() => handleCheckboxChange(option.value)}
                  />
                  <span className="text-gray-700 text-sm">{option.label}</span>
                </label>
              ))}
            </div>
            {formik.touched?.authStrategies &&
              formik.errors?.authStrategies && (
                <Error text={formik.errors?.authStrategies} />
              )}
          </div> */}

          <div className="col-span-2">
            <Label>Platforms Subscribed</Label>
            <div className="flex flex-wrap gap-4 p-4 justify-start">
              {platforms.map((product) => (
                <div
                  key={product.name}
                  className={`border rounded-lg flex flex-col items-center justify-center p-4 w-[120px] h-[100px] border-gray-100 gap-2 ${
                    !product.access
                      ? "cursor-not-allowed bg-black/10"
                      : "cursor-pointer hover:bg-[var(--hover-bg)]"
                  } ${product?.className}`}
                  onClick={() => {
                    handleRedirect(
                      product?.redirectURL || "",
                      product.platform,
                      product.analytics
                    );
                  }}
                  style={
                    {
                      "--hover-bg": product.bgColor,
                    } as React.CSSProperties
                  }
                >
                  <div className="relative w-10 h-10">
                    <img
                      src={product.icon}
                      className="w-full h-full object-contain"
                    />
                    {!product.access && (
                      <div className="absolute inset-0 bg-black/10 flex items-center justify-center rounded">
                        <Icon
                          icon="ic:baseline-lock"
                          className="text-gray-600"
                          height={32}
                          width={32}
                        />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-center">{product.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
